import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { createSearchParams, useNavigate } from 'react-router'
import { z } from 'zod'
import { MarqueeBreadcrumbs } from '@/components/commons/marquee-breadcrumbs'
import { Button } from '@/components/ui/button'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle
} from '@/components/ui/empty'
import { Image03Icon, PlusIcon, Settings04Icon } from '@/components/ui/icons'
import { CreatorOnlyNotice } from '@/features/marquee/components/creator-only-notice'
import {
	type LocationConfirmAction,
	LocationConfirmDialog
} from '@/features/marquee/components/location-confirm-dialog'
import {
	LocationDialog,
	type LocationDialogTarget
} from '@/features/marquee/components/location-dialog'
import { LocationsTable } from '@/features/marquee/components/locations-table'
import { MarqueeTableCard } from '@/features/marquee/components/marquee-table-card'
import { SearchInput } from '@/features/marquee/components/search-input'
import {
	type TeamFilterOption,
	TeamFilterSelect
} from '@/features/marquee/components/team-filter-select'
import {
	locationsQueryOptions,
	operatorQueryOptions
} from '@/features/marquee/query-options'
import { MARQUEE_LOCATION_STATUS_OPTIONS } from '@/features/marquee/types'
import {
	isMarketManagerAllowed,
	type LocationFilters,
	type LocationRowAction,
	locationRowActions,
	locationRowMatchesFilters,
	sortLocationsActiveFirst
} from '@/features/marquee/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import { routes } from '@/routes'
import type { MarqueeLocation } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

const STATUS_FILTER_OPTIONS = [
	{ id: 'all', label: 'marquee:locations_filter_all' },
	{ id: 'active', label: 'marquee:locations_status_active' },
	{ id: 'deactivated', label: 'marquee:locations_status_deactivated' }
] as const satisfies readonly TeamFilterOption<LocationFilters['status']>[]

const MarqueeLocationsSearchParamsSchema = z.object({
	search: z.string().optional(),
	status: z.enum(['all', ...MARQUEE_LOCATION_STATUS_OPTIONS]).catch('all')
})

/**
 * The one Locations dialog open at a time: the add / edit form, or a
 * confirmation. Held here rather than in the row menu, so the edit form's
 * Deactivate can hand over to the very confirmation the menu opens.
 */
type LocationsDialog =
	| { kind: 'form'; target: LocationDialogTarget }
	| {
			kind: 'confirm'
			action: LocationConfirmAction
			location: MarqueeLocation
	  }

function PageShell({ children }: { children: React.ReactNode }) {
	const { t } = useTranslation()
	return (
		<div className='flex flex-1 flex-col'>
			<title>{t('marquee:page_title_locations')}</title>
			<div className='border-neutral-200 border-b l:px-10 px-4 pt-6 pb-6'>
				<MarqueeBreadcrumbs />
			</div>
			{children}
		</div>
	)
}

/**
 * Custom locations for the event form's market picker (OPSSTD-10100): named
 * places inside a market, each with its own time zone. Add, edit and
 * deactivate / reactivate.
 *
 * Admin work, asked through `createLocation` (never the role) before anything
 * is fetched: anyone without it arriving by URL gets the notice and sends no
 * `include_inactive` request. The account-menu entry is hidden alongside.
 *
 * The list comes down whole, active and deactivated, and the search and the
 * status filter run here on the one cached list; both live in the URL.
 * Deactivated rows sort after the active ones.
 *
 * An empty list is its own state, not a filter that matched nothing: no
 * search or filter to offer, and the add button reads as the first step.
 */
function MarqueeLocations() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { searchParams, setSearchParams } = useParsedSearchParams(
		MarqueeLocationsSearchParamsSchema
	)
	const [dialog, setDialog] = useState<LocationsDialog | null>(null)

	const { data: operator } = useQuery(operatorQueryOptions())
	const isAllowedToView = isMarketManagerAllowed(operator, 'createLocation')

	const { data, isLoading, isError } = useQuery({
		...locationsQueryOptions({ includeInactive: true }),
		enabled: isAllowedToView
	})

	const locations = useMemo(() => {
		const filters: LocationFilters = {
			query: searchParams.search ?? '',
			status: searchParams.status
		}
		return sortLocationsActiveFirst(
			(data?.items ?? []).filter((location) =>
				locationRowMatchesFilters(location, filters)
			)
		)
	}, [data, searchParams.search, searchParams.status])

	const isListEmpty = !(isLoading || isError) && data?.items.length === 0

	// Stable so the table's column memo is not rebuilt on every render.
	const onAction = useCallback(
		(action: LocationRowAction, location: MarqueeLocation) => {
			if (action === 'view_events') {
				navigate({
					pathname: routes.MARQUEE_EVENTS.handle.fullPath,
					search: createSearchParams({
						search: location.name
					}).toString()
				})
				return
			}
			setDialog(
				action === 'edit'
					? { kind: 'form', target: { mode: 'edit', location } }
					: { kind: 'confirm', action, location }
			)
		},
		[navigate]
	)

	const closeDialog = () => setDialog(null)
	const editedLocation =
		dialog?.kind === 'form' && dialog.target.mode === 'edit'
			? dialog.target.location
			: undefined

	if (!isAllowedToView) {
		return (
			<PageShell>
				<CreatorOnlyNotice
					backLabel={t('marquee:creator_only_back_to_events')}
					backTo={routes.MARQUEE_EVENTS.handle.fullPath}
					description={t('marquee:locations_admin_only_description')}
					title={t('marquee:locations_admin_only_title')}
				/>
			</PageShell>
		)
	}

	return (
		<PageShell>
			<div className='flex l:flex-row flex-col l:items-start l:justify-between gap-4 l:px-10 px-4 pt-4 pb-8'>
				<div className='flex min-w-0 flex-col gap-1'>
					<h1 className='font-medium text-2xl text-neutral-900'>
						{t('marquee:locations_title')}
					</h1>
					<p className='max-w-3xl text-neutral-500 text-sm/4.5'>
						{t('marquee:locations_description')}
					</p>
				</div>

				<div className='flex shrink-0 flex-wrap items-center gap-3'>
					<Button
						onClick={() =>
							setDialog({
								kind: 'form',
								target: { mode: 'create' }
							})
						}
						type='button'
					>
						<PlusIcon />
						{t('marquee:locations_add')}
					</Button>
				</div>
			</div>

			<div className='flex flex-col gap-6 l:px-10 px-4 pb-10'>
				{isListEmpty ? (
					<Empty className='rounded-airbnb-2xl border border-grey-200 py-16'>
						<EmptyHeader className='gap-2'>
							<EmptyMedia
								className='size-19 [&>svg]:size-8'
								variant='icon'
							>
								<Image03Icon className='text-neutral-500' />
							</EmptyMedia>
							<EmptyTitle>
								{t('marquee:locations_no_locations_title')}
							</EmptyTitle>
							<EmptyDescription className='max-w-100'>
								{t(
									'marquee:locations_no_locations_description'
								)}
							</EmptyDescription>
						</EmptyHeader>
					</Empty>
				) : (
					<>
						<div className='flex l:flex-row flex-col l:items-center l:justify-between gap-4'>
							<SearchInput
								className='l:w-120 w-full'
								clearLabel={t('common:clear_search')}
								onChange={(value) =>
									setSearchParams({
										search: value || undefined
									})
								}
								placeholder={t(
									'marquee:locations_search_placeholder'
								)}
								value={searchParams.search ?? ''}
							/>
							<TeamFilterSelect
								icon={<Settings04Icon />}
								label='marquee:locations_filter_label'
								onChange={(status) =>
									setSearchParams({
										status:
											status === 'all'
												? undefined
												: status
									})
								}
								options={STATUS_FILTER_OPTIONS}
								value={searchParams.status}
							/>
						</div>

						<MarqueeTableCard>
							<LocationsTable
								data={locations}
								isError={isError}
								isLoading={isLoading}
								onAction={onAction}
							/>
						</MarqueeTableCard>
					</>
				)}
			</div>

			{/* Keyed on the row so each open starts from its own values. */}
			{dialog?.kind === 'form' && (
				<LocationDialog
					key={
						dialog.target.mode === 'edit'
							? dialog.target.location.id
							: 'create'
					}
					onClose={closeDialog}
					onDeactivate={
						editedLocation &&
						locationRowActions(editedLocation, operator).includes(
							'deactivate'
						)
							? () => onAction('deactivate', editedLocation)
							: undefined
					}
					target={dialog.target}
				/>
			)}
			{dialog?.kind === 'confirm' && (
				<LocationConfirmDialog
					action={dialog.action}
					key={`${dialog.action}:${dialog.location.id}`}
					location={dialog.location}
					onClose={closeDialog}
				/>
			)}
		</PageShell>
	)
}

export { MarqueeLocations }
